import { createContext, useContext, useEffect, useState } from 'react';
import { getMyThreads, MessageThread } from '../services/MessagingService';
import { useApiKey } from './ApiKeyContext';
import { isFeatureEnabled } from '../featureFlags';

type ThreadsContextType = {
  threads: MessageThread[];
  refreshThreads: () => Promise<void>;
};

const ThreadsContext = createContext<ThreadsContextType | undefined>(undefined);

export const ThreadsProvider = ({ children }: { children: React.ReactNode }) => {
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const { apiKey } = useApiKey();

  const refreshThreads = async () => {
    if (!apiKey) return;
    if (!isFeatureEnabled('messaging')) return;
    try {
      const result = await getMyThreads(apiKey);
      setThreads(result);
    } catch (error) {
      console.error('Failed to load threads', error);
    }
  };

  useEffect(() => {
    if (!isFeatureEnabled('messaging')) {
      setThreads([]);
      return;
    }
    refreshThreads();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshThreads();
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [apiKey]);

  return <ThreadsContext.Provider value={{ threads, refreshThreads }}>{children}</ThreadsContext.Provider>;
};

export const useThreads = () => {
  const context = useContext(ThreadsContext);
  if (!context) {
    throw new Error('useThreads must be used within a ThreadsProvider');
  }
  return context;
};
